import { type PermissionKey } from '@aysar/contracts';
import { type ReactNode } from 'react';

import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';

export interface PermissionGateProps {
  /** Allowed when the operator holds this key. */
  permission?: PermissionKey;
  /** Allowed when the operator holds ANY of these. */
  anyOf?: readonly PermissionKey[];
  /** Allowed when the operator holds EVERY one of these. */
  allOf?: readonly PermissionKey[];
  /** Rendered instead of the children. Defaults to nothing at all. */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Renders its children only for an operator who holds the permission.
 *
 * This is a **courtesy, not a control**. The api is the authority (N22): every
 * endpoint behind a gated button is guarded independently, and hiding the button
 * only spares the operator a click that was going to be refused. Nothing may
 * ever be protected by this component alone.
 *
 * It evaluates through `useHasPermission`, which is the single client evaluator
 * calling the very functions the server's guard calls — so wildcards and
 * deny-wins are decided the same way in both places. A hand-rolled
 * `permissions.includes(key)` here would get deny semantics wrong on its first
 * day and would show a button the api then refuses.
 *
 * The default fallback is nothing, because the honest answer to "may I?" is
 * usually silence: an operator without `access.manage` should see a roles page
 * with no Delete column, not a row of disabled buttons advertising what they
 * cannot do. Pass `fallback` when the absence would be confusing instead.
 */
export function PermissionGate({
  permission,
  anyOf,
  allOf,
  fallback = null,
  children,
}: PermissionGateProps) {
  const { can, canAll, canAny } = useHasPermission();

  const checks: boolean[] = [];

  if (permission !== undefined) {
    checks.push(can(permission));
  }

  if (anyOf !== undefined && anyOf.length > 0) {
    checks.push(canAny(anyOf));
  }

  if (allOf !== undefined && allOf.length > 0) {
    checks.push(canAll(allOf));
  }

  // No requirement stated is an authoring mistake, and the safe reading of a
  // mistake in an access check is "no". A gate that defaulted to open would be
  // invisible in review and would fail open on a typo'd prop name.
  const isAllowed = checks.length > 0 && checks.every(Boolean);

  return <>{isAllowed ? children : fallback}</>;
}
